import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    country: user?.country || "",
    bio: user?.bio || "",
    subjects: (user?.subjects || []).join(", "),
    grades: (user?.grades || []).join(", "),
    teaching_language: user?.teaching_language || "English",
    availability: user?.availability || "",
    preferred_meeting_platform: user?.preferred_meeting_platform || "google_meet",
    google_email: user?.google_email || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = {
        ...form,
        subjects: form.subjects.split(",").map((s) => s.trim()).filter(Boolean),
        grades: form.grades.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.patch("/teacher/profile", payload);
      setUser(data.user);
      toast.success("Saved");
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const isTeacher = user?.role === "teacher" || user?.role === "admin";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ settings</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">Profile & preferences</h1>
      </div>

      <form onSubmit={save} className="bg-white border border-neutral-200 p-6 space-y-3">
        <Field label="Full name"><input className={inp} required value={form.full_name} onChange={set("full_name")} /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Phone"><input className={inp} value={form.phone} onChange={set("phone")} /></Field>
          <Field label="Country"><input className={inp} value={form.country} onChange={set("country")} /></Field>
        </div>
        {isTeacher && (
          <>
            <Field label="Subjects (comma separated)"><input className={inp} value={form.subjects} onChange={set("subjects")} /></Field>
            <Field label="Grades (comma separated)"><input className={inp} value={form.grades} onChange={set("grades")} /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Teaching language"><input className={inp} value={form.teaching_language} onChange={set("teaching_language")} /></Field>
              <Field label="Preferred meeting platform">
                <select className={inp} value={form.preferred_meeting_platform} onChange={set("preferred_meeting_platform")}>
                  <option value="google_meet">Google Meet (built-in)</option>
                  <option value="jitsi">TS Platform (legacy)</option>
                  <option value="zoom">Zoom</option>
                  <option value="meet">Google Meet</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="custom">Custom URL</option>
                </select>
              </Field>
            </div>
            <Field label="Google account email — for Meet recording">
              <input className={inp} type="email" value={form.google_email} onChange={set("google_email")} placeholder="you@gmail.com" data-testid="settings-google-email" />
              <p className="text-[11px] text-neutral-500 mt-1">To record classes, this must be a Google account inside your school's Workspace organization (same domain as the platform's Google account). Join the Meet signed in with it and the Record option appears under ⋮ → Manage recording.</p>
            </Field>
            <Field label="Availability"><input className={inp} value={form.availability} onChange={set("availability")} placeholder="Mon-Fri 4-9pm IST" /></Field>
            <Field label="Bio"><textarea className={inp} rows={4} value={form.bio} onChange={set("bio")} /></Field>
            {user?.profile_slug && (
              <div className="text-xs text-neutral-500">
                Your public profile URL: <code className="bg-neutral-100 px-2 py-0.5">{window.location.origin}/p/{user.profile_slug}</code>
              </div>
            )}
          </>
        )}
        <button data-testid="settings-save" type="submit" disabled={loading} className="bg-[#0A0C10] text-white font-bold px-6 py-3 disabled:opacity-60">{loading ? "Saving…" : "Save changes"}</button>
      </form>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
